import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Route, AlertTriangle, Loader2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import eventService from '@services/eventService';
import { Event, RoutePlanningInvitation } from '@/types/event';

// Planejamento de rota dos convites (logística própria): escolha o evento e a cidade,
// veja os endereços no mapa (mesmo modelo do gerenciamento de rotas), desmarque os que
// ficam de fora e crie a rota — a otimização de km é a já existente (botão Otimizar da
// rota, Google Route Optimization).

const createNumberedIcon = (number: number, selected: boolean) => {
  const color = selected ? '#3B82F6' : '#94A3B8';
  return L.divIcon({
    className: '',
    html: `<div style="
      background: ${color};
      color: white;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 12px;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

const FitBounds: React.FC<{ points: [number, number][] }> = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
};

export const RoutePlanningView: React.FC = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState<Event[]>([]);
  const [eventId, setEventId] = useState<number | ''>('');
  const [cities, setCities] = useState<string[]>([]);
  const [city, setCity] = useState('');
  const [invitations, setInvitations] = useState<RoutePlanningInvitation[]>([]);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    eventService.listEvents(1, 100).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  // Evento escolhido → carrega as cidades com convites pendentes (logística própria).
  useEffect(() => {
    setCities([]);
    setCity('');
    setInvitations([]);
    setChecked({});
    if (!eventId) return;
    setLoading(true);
    setError(null);
    eventService.getRoutePlanning(Number(eventId))
      .then((r) => setCities(r.cities))
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar cidades'))
      .finally(() => setLoading(false));
  }, [eventId]);

  // Cidade escolhida → carrega e geocodifica os convites dela.
  useEffect(() => {
    setInvitations([]);
    setChecked({});
    if (!eventId || !city) return;
    setLoading(true);
    setError(null);
    eventService.getRoutePlanning(Number(eventId), city)
      .then((r) => {
        const items = r.invitations ?? [];
        setInvitations(items);
        // Todos marcados por padrão; desmarcar remove do mapa e da rota.
        setChecked(Object.fromEntries(items.map((i) => [i.delivery_order_id, true])));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar convites'))
      .finally(() => setLoading(false));
  }, [eventId, city]);

  const selectedInvitations = invitations.filter((i) => checked[i.delivery_order_id]);
  const mappablePoints = useMemo<[number, number][]>(
    () => selectedInvitations
      .filter((i) => i.latitude != null && i.longitude != null)
      .map((i) => [i.latitude!, i.longitude!]),
    [selectedInvitations],
  );

  const handleCreateRoute = () => {
    const ids = selectedInvitations.map((i) => i.delivery_order_id);
    if (ids.length === 0) return;
    // Pré-carrega o formulário de rota (Gerenciar Rotas) com as paradas tipo D; o
    // usuário escolhe motorista/veículo lá e otimiza a rota depois de criada.
    navigate('/driver-routes/new', { state: { preselectedDeliveryOrderIds: ids } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1">Planejamento de Rota — Convites</h1>
        <p className="text-slate-600">Escolha o evento e a cidade; desmarque os convites que ficam de fora e crie a rota de entrega</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <select
              className="input-field text-sm md:w-80"
              value={eventId}
              onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Selecione o evento...</option>
              {events.map((ev) => (
                <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>
              ))}
            </select>
            <select
              className="input-field text-sm md:w-64"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={!eventId || cities.length === 0}
            >
              <option value="">{cities.length === 0 ? 'Sem convites pendentes' : 'Selecione a cidade...'}</option>
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="flex-1" />
            <Button
              onClick={handleCreateRoute}
              disabled={selectedInvitations.length === 0}
              icon={<Route size={18} />}
            >
              Criar Rota ({selectedInvitations.length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center gap-2 py-8 text-slate-400">
          <Loader2 size={18} className="animate-spin" /> Carregando...
        </div>
      )}

      {city && !loading && (
        <>
          {/* Mapa (mesmo modelo do gerenciamento de rotas: Leaflet + OSM) */}
          <Card>
            <CardHeader title={<span className="flex items-center gap-2"><MapPin size={16} className="text-emerald-600" /> Mapa — {city}</span>} />
            <CardContent className="p-0">
              <div className="h-[420px] rounded-b-xl overflow-hidden">
                <MapContainer center={[-23.55, -46.63]} zoom={11} className="h-full w-full" scrollWheelZoom>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds points={mappablePoints} />
                  {selectedInvitations
                    .filter((i) => i.latitude != null && i.longitude != null)
                    .map((inv, idx) => (
                      <Marker
                        key={inv.delivery_order_id}
                        position={[inv.latitude!, inv.longitude!]}
                        icon={createNumberedIcon(idx + 1, true)}
                      >
                        <Popup>
                          <strong>{inv.recipient_name}</strong>
                          <br />
                          {inv.address_text}
                        </Popup>
                      </Marker>
                    ))}
                </MapContainer>
              </div>
            </CardContent>
          </Card>

          {/* Lista com checkboxes */}
          <Card>
            <CardHeader
              title={`Convites em ${city} (${invitations.length})`}
              subtitle="Desmarcar remove o endereço do mapa e da rota."
            />
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {invitations.map((inv) => (
                  <label key={inv.delivery_order_id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-emerald-600 border-slate-300 rounded"
                      checked={Boolean(checked[inv.delivery_order_id])}
                      onChange={(e) => setChecked((c) => ({ ...c, [inv.delivery_order_id]: e.target.checked }))}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm">{inv.recipient_name}</p>
                      <p className="text-xs text-slate-500 truncate">{inv.address_text}</p>
                      {inv.geocoding_status === 1 && (
                        <p className="inline-flex items-center gap-1 text-xs text-amber-700 mt-0.5">
                          <AlertTriangle size={12} /> {inv.geocoding_message || 'Endereço não geocodificado — não aparece no mapa'}
                        </p>
                      )}
                    </div>
                    <span className="font-mono text-xs text-slate-400">#{inv.delivery_order_id}</span>
                  </label>
                ))}
                {invitations.length === 0 && (
                  <p className="px-4 py-8 text-center text-sm text-slate-400">Nenhum convite pendente nesta cidade.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
